import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ArrowUpDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export interface SortOption {
  value: string;
  label: string;
}

interface NexoraXSortDropdownProps {
  options?: SortOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  className?: string;
}

const DEFAULT_OPTIONS: SortOption[] = [
  { value: 'priority', label: 'Node Priority' },
  { value: 'name', label: 'Name' },
  { value: 'lastModified', label: 'Last Modified' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'recent', label: 'Recently Used' },
  { value: 'views', label: 'Most Viewed' },
  { value: 'priority_desc', label: 'Priority High to Low' }
];

export function NexoraXSortDropdown({
  options = DEFAULT_OPTIONS,
  selectedValue,
  onSelect,
  className = ''
}: NexoraXSortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const currentOption = options.find(opt => opt.value === selectedValue) || options[0];

  return (
    <div 
      ref={dropdownRef} 
      className={`relative inline-block text-left select-none ${className}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-md border border-cyan-500/30 rounded-xl hover:border-cyan-400 hover:shadow-[0_0_12px_rgba(6,182,212,0.25)] transition-all duration-300 cursor-pointer text-xs font-mono text-slate-300 outline-none focus:outline-none focus:ring-1 focus:ring-cyan-400"
      >
        <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Sort by:</span>
        <span className="font-extrabold text-cyan-300 uppercase tracking-tight truncate max-w-[130px]">
          {currentOption?.label}
        </span>
        <ChevronDown 
          size={12} 
          className={`text-cyan-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl border border-cyan-500/30 bg-[#060312]/95 backdrop-blur-xl shadow-[0_4px_25px_rgba(232,64,243,0.15)] focus:outline-none z-50 overflow-hidden"
            role="listbox"
          >
            <div className="py-1.5 divide-y divide-purple-950/20 max-h-72 overflow-y-auto">
              {options.map((option) => {
                const isActive = option.value === selectedValue;
                return (
                  <button
                    key={option.value}
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      onSelect(option.value);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2 text-[11px] font-mono uppercase tracking-wide transition-all duration-200 flex items-center justify-between cursor-pointer outline-none ${
                      isActive 
                        ? 'text-amber-400 font-extrabold bg-[#e840f3]/10' 
                        : 'text-slate-400 hover:text-[#e840f3] hover:bg-[#e840f3]/5'
                    }`}
                  >
                    <span>{option.label}</span>
                    {isActive ? (
                      <Check size={11} className="text-amber-400" />
                    ) : (
                      <div className="w-2.5 h-2.5 rounded-full border border-zinc-805" />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
